import React, { useEffect, useState } from "react";
import Swal from 'sweetalert2';
import PropTypes from "prop-types";
import { Select } from 'antd';
import { EDIT_USER } from "../../services/userServices";
import { BASE_API_URL } from "../../services/baseServices";
import { GET_MANAGERS, GET_ROLES } from "../../services/commonServices";
import { updateSupplier } from "../../services/supplierServices";

export default function EditSupplier({ editObj }) {

  const { Option } = Select;
  const [sessionUserData, setsessionUserData] = useState({});
  const [rolesData, setRolesData] = useState([]);
  const [editSubmitLoading, setEditSubmitLoading] = useState(false);
  const [manager, setManager] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [emailError, setEmailError] = useState('');

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const [formData, setFormData] = useState({
        SupplierId: '',
        OrgId: sessionUserData?.OrgId,
        SupplierName: "",
        Phone: "",
        Email: true,
        Address: "",
        GSTNumber: "",
        PANNumber: "",
        IsActive: "",
        UpdatedBy: sessionUserData?.Id,
  });

  // Fetch session user data
  useEffect(() => {
    const userDataString = sessionStorage.getItem("userData");
    if (userDataString) {
      const userData = JSON.parse(userDataString);
      setsessionUserData(userData);
      setFormData((prev) => ({
        ...prev,
        OrgId: userData.OrgId,
        UpdatedBy: userData.Id
      }));
    }
  }, []);

  useEffect(() => {
    if (editObj) {
      setFormData({
        SupplierId: editObj.Id,
        OrgId: editObj?.OrgId,
        SupplierName: editObj?.SupplierName,
        Phone: editObj?.Phone,
        Email: editObj?.Email,
        Address: editObj?.Address,
        GSTNumber: editObj?.GSTNumber,
        PANNumber: editObj?.PANNumber,
        IsActive: editObj?.IsActive ? 1 : 0,
      });
    }
  }, [editObj]);

  const handleInputChange = (e) => {
    setEmailError("");
    const { name, value, type, checked } = e.target;

    if (name === 'Phone') {
      // Allow only digits, max 10 characters
      if (!/^\d{0,10}$/.test(value)) {
        Swal.fire({
          title: "Invalid Input",
          text: "Please enter only numbers (max 10 digits) without letters or special characters.",
          icon: "error",
        });
        return;
      }
    }

    const formattedValue = (name === 'SupplierName')
      ? toTitleCase(value)
      : value;

    setFormData((prevState) => ({
      ...prevState,
      [name]: type === "checkbox" ? checked : formattedValue,
    }));
  };


  const validateEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.(com|in|gov|tech|info|org|net|us|edu|shop|dev)$/i;
    return regex.test(email);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setEditSubmitLoading(true);
  
    if (!/^\d{10}$/.test(formData.Phone)) {
      Swal.fire({
        title: "Invalid Mobile Number",
        text: "Mobile number must be exactly 10 digits.",
        icon: "error",
      });
      setEditSubmitLoading(false);
      return;
    }
  
    if (!validateEmail(formData.Email)) {
      setEmailError('Please enter a valid email ending with ex: .com or .in');
      setEditSubmitLoading(false);
      return;
    }

    formData.UpdatedBy = 1;
  
    try {
      const response = await updateSupplier(formData);
  
      if (response?.data?.ResultData?.Status === 'Success') {
        Swal.fire({
          title: "Success",
          text: "Supplier has been updated successfully.",
          icon: "success",
        }).then(() => window.location.reload());
      } else {
        Swal.fire({
          title: "Error",
          text: response?.data?.ResultData?.ResultMessage || "Failed to update supplier.",
          icon: "error",
        });
      }
    } catch (error) {
      console.error("Error during submission:", error.message);
      Swal.fire({
        title: "Error",
        text: "An unexpected error occurred.",
        icon: "error",
      });
    } finally {
      setEditSubmitLoading(false);
    }
  };

  const toTitleCase = (str) => {
    return str
      .split(/([.\s])/g) // Split and preserve dots and spaces
      .map(part =>
        /[a-zA-Z]/.test(part) ? part.charAt(0).toUpperCase() + part.slice(1).toLowerCase() : part
      )
      .join('');
  };


  return (
    <div
      className="offcanvas offcanvas-end"
      tabIndex="-1"
      id="offcanvasRightEdit"
      aria-labelledby="offcanvasRightLabel"
      style={{ maxWidth: "45%" }}
    >
      <form onSubmit={handleSubmit}>
        <div className="offcanvas-header d-flex justify-content-between align-items-center">
          <h5 id="offcanvasRightLabel" className="mb-0">Edit Supplier</h5>
          <div className="d-flex align-items-center">
            <button className="btn btn-primary btn-sm me-2" type="submit" disabled={editSubmitLoading}>
              {editSubmitLoading ? "Submitting..." : "Submit"}
            </button>
            <button
              type="button"
              className="btn-close"
              data-bs-dismiss="offcanvas"
              aria-label="Close"
            ></button>
          </div>
        </div>
        <div className="offcanvas-body">
          <div className="row">
            <div className="col-6 mb-2">
              <label className="form-label">Name<span className="text-danger">*</span></label>
              <input
                type="text"
                name="SupplierName"
                className="form-control"
                placeholder="Enter supplier name"
                value={formData.SupplierName}
                onChange={handleInputChange}
                onKeyDown={(e) => {
                  if (e.key === ' ') {
                    e.preventDefault();
                  }
                }}
                required
              />
            </div>
            <div className="col-6 mb-2">
              <label className="form-label">Mobile<span className="text-danger">*</span></label>
              <div className="input-group">
                <span className="input-group-text">🇮🇳 +91</span>
                <input
                  type="tel"
                  name="Phone"
                  className="form-control"
                  placeholder="Enter 10-digit mobile number"
                  value={formData.Phone}
                  onChange={handleInputChange}
                  maxLength={10}
                  required
                />
              </div>
            </div>

            <div className="col-6 mb-2">
              <label className="form-label">Email<span className="text-danger">*</span></label>
              <input
                type="email"
                name="Email"
                className={`form-control ${emailError ? 'is-invalid' : ''}`}
                placeholder="Enter email address"
                value={formData.Email}
                onChange={handleInputChange}
                required
              />
              {emailError && <div className="invalid-feedback">{emailError}</div>}
            </div>
            <div className="col-6 mb-2">
              <label className="form-label">GST Number<span className="text-danger">*</span></label>
              <input
                type="text"
                name="GSTNumber"
                className={`form-control`}
                placeholder="Enter GST Number"
                value={formData.GSTNumber}
                onChange={handleInputChange}
              />
            </div>
            <div className="col-6 mb-2">
              <label className="form-label">PAN Number<span className="text-danger">*</span></label>
              <input
                type="text"
                name="PANNumber"
                className={`form-control`}
                placeholder="Enter GST Number"
                value={formData.PANNumber}
                onChange={handleInputChange}
              />
            </div>
            <div className="col-12">
              <label className="form-label">Address<span className="text-danger">*</span></label>
              <textarea
                rows={4}
                name="Address"
                className={`form-control`}
                placeholder="Enter address..."
                value={formData.Address}
                onChange={handleInputChange}
              />
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}

EditSupplier.propTypes = {
  editObj: PropTypes.object.isRequired,
};
